// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { createSelector } from 'reselect';

export const orderCreationSelector = createSelector(
    (state): any => state,
    (res: { order: any }) => res.order
);

export const selectedDataSelector = createSelector(
    orderCreationSelector,
    order => order.selection
);

export const selectedParentSelector = createSelector(
    selectedDataSelector,
    res => res.parent
);

export const selectedAccessorySelector = createSelector(
    selectedDataSelector,
    res => res.accessory || []
);

export const customerInfoSelector = createSelector(
    orderCreationSelector,
    res => res.customerInfo
);

export const jarvisInfoSelector = createSelector(
    orderCreationSelector,
    res => res.jarvis
);

export const registerInfoSelector = createSelector(
    orderCreationSelector,
    res => res.registration
);

export const zipCodeSelector = createSelector(
    orderCreationSelector,
    res => res.zipCode
);

export const selectedCountryCodeSelector = createSelector(
    orderCreationSelector,
    res => res.countryCode
);

export const progressSelector = createSelector(
    orderCreationSelector,
    res => res.currentStage
);

const repairItemSelector = createSelector(
    orderCreationSelector,
    res => res.repairItems
);

export const isFullDeviceRepairSelector = createSelector(
    repairItemSelector,
    res => res.isFullDeviceRepair || false
);

export const isSoftwareOrderSelector = createSelector(
    repairItemSelector,
    res => res.isSoftwareOrder || false
);

export const deviceRepairSelector = createSelector(
    repairItemSelector,
    res => res.deviceItems
);

export const deviceRepairItemSelector = createSelector(
    repairItemSelector,
    res => (sku: string): any => res.deviceItems[sku]
);

export const accessoryRepairSelector = createSelector(
    repairItemSelector,
    res => res.accessories
);

const offerSelector = createSelector(
    orderCreationSelector,
    res => res.repairOffer
);

export const parentDeviceOfferSelector = createSelector(
    offerSelector,
    res => res.fullDeviceRepairOffer
);

export const softwareRepairOfferSelector = createSelector(
    offerSelector,
    res => res.softwareRepairOffer
);

export const softwareRepairDetailsSelector = createSelector(
    orderCreationSelector,
    res => res.softwareRepairDetails
);

export const partAndAccessoryOfferSelector = createSelector(
    offerSelector,
    res => res.accessoryAndPartRepairOffer
);

export const selectedOfferSelector = createSelector(
    orderCreationSelector,
    res => (symptomCode: string) => res.selectedRepairOffers[symptomCode]
);

export const allSelectedOfferSelector = createSelector(
    orderCreationSelector,
    res => res.selectedRepairOffers
);

export const selectConfirmation = createSelector(
    orderCreationSelector,
    res => res.confirmation
);

export const shippingAddressSelector = createSelector(
    orderCreationSelector,
    res => res.shippingAddress || {}
);

export const billingAddressSelector = createSelector(
    orderCreationSelector,
    res => res.billingAddress || {}
);

export const replacePartsSelector = createSelector(
    orderCreationSelector,
    res => res.parts
);

export const accessoryShippingOfferSelector = createSelector(
    orderCreationSelector,
    res => res.accessoryShippingOffers || []
);

export const deviceShippingOfferSelector = createSelector(
    orderCreationSelector,
    res => res.deviceShippingOffers || []
);

export const shippingOfferSelector = createSelector(
    orderCreationSelector,
    res => [
        ...(res.deviceShippingOffers || []),
        ...(res.accessoryShippingOffers || []),
    ]
);

export const selectedShippingOfferSelector = createSelector(
    orderCreationSelector,
    res => res.selectedShippingOffer || []
);

export const safetyIssueInfoSelector = createSelector(
    orderCreationSelector,
    res => res.safetyIssueInfo || {}
);

export const reActivateOrderStatusSelector = createSelector(
    orderCreationSelector,
    res => res.reactivateOrder
);

export const offerReasonsSelector = createSelector(
    orderCreationSelector,
    res => res.reasonForNotSelectingOffers
);

export const z4rReturnReasonsSelector = createSelector(
    orderCreationSelector,
    res => res.reasonForZ4RReturn
);

export const isOnSiteRepairStatusSelector = createSelector(
    orderCreationSelector,
    res => res.isOnSiteRepair
);

export const customerCommentsSelector = createSelector(
    orderCreationSelector,
    res => res.customerComments
);

export const customerServiceLocationAddressSelector = createSelector(
    orderCreationSelector,
    res => res.customerServiceLocationAddress
);

export const secondSRCreationSelector = createSelector(
    orderCreationSelector,
    res => res.secondSRCreation
);